import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiRequestError } from '../api/api.service';
import { TradeSystemError } from '../trade-system';

@Injectable({
  providedIn: 'root',
})
export class ErrorDumpService {
  dumpError(error: unknown): Record<string, unknown> {
    return this.dumpUnknown(error, new WeakSet())
  }

  private dumpUnknown(error: unknown, seen: WeakSet<object>): Record<string, unknown> {
    if (error instanceof ApiRequestError) {
      return {
        type: 'ApiRequestError',
        message: error.message,
        stack: error.stack,
        timestamp: error.timestamp,
        httpError: this.dumpUnknown(error.httpError, seen),
        apiError: this.dumpValue(error.apiError, seen),
        requestInfo: this.dumpValue(error.requestInfo, seen),
      }
    }
    if (error instanceof TradeSystemError) {
      return {
        type: 'TradeSystemError',
        message: error.message,
        stack: error.stack,
        timestamp: error.timestamp,
        errorData: this.dumpValue(error.errorData, seen),
        requestInfo: this.dumpValue(error.requestInfo, seen),
      }
    }
    if (error instanceof HttpErrorResponse) {
      return {
        type: error.name,
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        error: this.dumpValue(error.error, seen),
      }
    }
    if (error instanceof ErrorEvent) {
      return {
        type: 'ErrorEvent',
        message: error.message,
        line: error.lineno,
        col: error.colno,
        filename: error.filename,
        error: this.dumpValue(error.error, seen),
      }
    }
    if (error instanceof PromiseRejectionEvent) {
      return {
        type: 'PromiseRejectionEvent',
        reason: this.dumpValue(error.reason, seen),
      }
    }
    if (error instanceof Error) {
      const dump: Record<string, unknown> = {
        type: error.name,
        message: error.message,
      }
      if (error.stack) {
        dump['stack'] = error.stack
      }
      if (error.cause !== undefined) {
        dump['cause'] = this.dumpValue(error.cause, seen)
      }
      return dump
    }
    if (typeof error == 'object' && error !== null) {
      return this.dumpObject(error, seen)
    }
    return {
      type: typeof error,
      data: String(error),
    }
  }

  private dumpValue(value: unknown, seen: WeakSet<object>): unknown {
    if (value === null || value === undefined) {
      return value
    }
    if (typeof value == 'string' || typeof value == 'number' || typeof value == 'boolean') {
      return value
    }
    if (typeof value == 'bigint' || typeof value == 'symbol' || typeof value == 'function') {
      return String(value)
    }
    if (
      value instanceof ApiRequestError
      || value instanceof TradeSystemError
      || value instanceof HttpErrorResponse
      || value instanceof ErrorEvent
      || value instanceof PromiseRejectionEvent
      || value instanceof Error
    ) {
      return this.dumpUnknown(value, seen)
    }
    if (Array.isArray(value)) {
      return value.map(item => this.dumpValue(item, seen))
    }
    if (typeof value == 'object') {
      return this.dumpObject(value, seen)
    }
    return String(value)
  }

  private dumpObject(value: object, seen: WeakSet<object>): Record<string, unknown> {
    if (seen.has(value)) {
      return { type: 'CircularReference' }
    }
    seen.add(value)

    const dump: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(value)) {
      dump[key] = this.dumpValue(nested, seen)
    }
    return dump
  }
}
